import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

/* ------------------- Filtros ------------------- */

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter((e.target.value ?? "").toUpperCase() || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

/* ------------------- Helpers ------------------- */

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "0,00";
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const calcTotalLinha = (q, pu) => {
  const nq = Number(q);
  const npu = Number(pu);
  if (!Number.isFinite(nq) || !Number.isFinite(npu)) return 0;
  return nq * npu;
};

/* ========================================================= */

export default function DialogEquipamentoEventoLinhas({ open, onClose, evento, onChanged }) {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  /* ===============================
     🔒 Congelar ID do evento
  =============================== */

  const [idEvento, setIdEvento] = useState(0);
  const [tituloEvento, setTituloEvento] = useState("");

  useEffect(() => {
    if (!open) return;

    const id = Number(evento?.id || 0);
    setIdEvento(id);
    setTituloEvento(evento?.titulo ? String(evento.titulo) : "");
  }, [open, evento]);

  /* =============================== */

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const TIPOS_LINHA = ["PEÇA", "SERVIÇO", "OUTRO"];

  const emptyForm = {
    id: 0,
    id_evento: 0,
    descritivo: "",
    quantidade: "",
    preco_unitario: "",
    tipo_linha: "PEÇA",
  };

  const [openEdit, setOpenEdit] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  // confirmar apagar
  const [openConfirmDel, setOpenConfirmDel] = useState(false);
  const [linhaParaApagar, setLinhaParaApagar] = useState(null);

  /* ===============================
     Carregar linhas
  =============================== */

  const carregar = async () => {
    if (!idEvento) {
      setData([]);
      return;
    }

    setLoading(true);

    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentoEventoLinhas", {
        id_evento: idEvento,
      })
    );

    setLoading(false);

    if (err) return showAlert(err, "error");

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

    setData(arr);
  };

  useEffect(() => {
    if (!open) return;
    carregar();
    // eslint-disable-next-line
  }, [open, idEvento]);

  /* ===============================
     Abrir/Fechar edição
  =============================== */

  const handleOpenNovo = () => {
    if (!temPermissao("Menu Equipamentos", "w")) return;
    if (!idEvento) return showAlert("Evento inválido.", "warning");

    setEditando(false);
    setForm({
      ...emptyForm,
      id_evento: idEvento,
      tipo_linha: "PEÇA",
    });

    setOpenEdit(true);
  };

  const handleOpenEditar = (ln) => {
    if (!temPermissao("Menu Equipamentos", "u")) return;

    setEditando(true);
    setForm({
      ...emptyForm,
      ...ln,
      id: ln.id,
      id_evento: ln.id_evento ?? idEvento,
      descritivo: (ln.descritivo ?? "").toUpperCase(),
      quantidade: ln.quantidade ?? "",
      preco_unitario: ln.preco_unitario ?? "",
      tipo_linha: (ln.tipo_linha ?? "PEÇA").toUpperCase(),
    });

    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditando(false);
    setForm({ ...emptyForm });
  };

  const handleCloseAll = () => {
    handleCloseEdit();

    setOpenConfirmDel(false);
    setLinhaParaApagar(null);
    setData([]);

    if (typeof onClose === "function") onClose();
  };

  /* ===============================
     Guardar linha
  =============================== */

  const handleSave = async () => {
    const idEv = Number(form.id_evento || 0);
    if (idEv <= 0) return showAlert("Evento obrigatório.", "warning");

    const desc = (form.descritivo ?? "").trim().toUpperCase();
    if (!desc) return showAlert("Descritivo obrigatório.", "warning");

    const qtd = Number(form.quantidade);
    const pu = Number(form.preco_unitario);

    if (!Number.isFinite(qtd) || qtd <= 0) return showAlert("Quantidade inválida.", "warning");

    if (!Number.isFinite(pu) || pu < 0) return showAlert("Preço unitário inválido.", "warning");

    setLoading(true);

    const body = {
      ...form,
      id_evento: idEv,
      descritivo: desc,
      tipo_linha: (form.tipo_linha ?? "OUTRO").toUpperCase(),
      quantidade: qtd,
      preco_unitario: pu,
    };

    const endpoint = editando
      ? "Equipamentos/UpdEquipamentoEventoLinha"
      : "Equipamentos/InsEquipamentoEventoLinha";

    const [_, err, meta] = await handleApi(api.post(endpoint, body));

    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();
    handleCloseEdit();

    if (typeof onChanged === "function") onChanged();
  };

  /* ===============================
     Apagar linha
  =============================== */

  const apagar = async (ln) => {
    if (!temPermissao("Menu Equipamentos", "d")) return;
    if (!ln?.id) return;

    setLoading(true);

    const [_, err, meta] = await handleApi(
      api.post("Equipamentos/DelEquipamentoEventoLinha", {
        id: ln.id,
        id_evento: idEvento,
      })
    );

    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();

    if (typeof onChanged === "function") onChanged();
  };

  const confirmarApagar = async () => {
    if (!linhaParaApagar?.id) return;

    await apagar(linhaParaApagar);

    setOpenConfirmDel(false);
    setLinhaParaApagar(null);
  };

  /* ===============================
     Tabela
  =============================== */

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 90,
        Cell: ({ row }) => {
          const ln = row.original;
          return (
            <div style={{ display: "flex", gap: 4 }}>
              <IconButton
                size="small"
                disabled={!temPermissao("Menu Equipamentos", "u")}
                onClick={() => handleOpenEditar(ln)}
                color="warning"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                disabled={!temPermissao("Menu Equipamentos", "d")}
                onClick={() => {
                  setLinhaParaApagar(ln);
                  setOpenConfirmDel(true);
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Tipo", accessor: "tipo_linha", Filter: TextFilter },
      { Header: "Descritivo", accessor: "descritivo", Filter: TextFilter },
      {
        Header: "Qtd",
        accessor: "quantidade",
        Filter: TextFilter,
        Cell: ({ value }) => fmtNum(value),
      },
      {
        Header: "Preço",
        accessor: "preco_unitario",
        Filter: TextFilter,
        Cell: ({ value }) => fmtMoney(value),
      },
      {
        Header: "Total",
        id: "total",
        disableFilters: true,
        Cell: ({ row }) =>
          fmtMoney(calcTotalLinha(row.original?.quantidade, row.original?.preco_unitario)),
      },
    ],
    [temPermissao]
  );

  const table = useTable(
    { columns, data, autoResetFilters: false, autoResetSortBy: false },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  const subtotalLinhas = useMemo(() => {
    return (data || []).reduce(
      (acc, ln) => acc + calcTotalLinha(ln?.quantidade, ln?.preco_unitario),
      0
    );
  }, [data]);

  /* ===============================
     Render
  =============================== */

  return (
    <>
      <Dialog open={open} onClose={handleCloseAll} maxWidth="lg" fullWidth>
        <DialogTitle>Linhas do Evento — {tituloEvento || `Evento #${idEvento}`}</DialogTitle>

        <DialogContent>
          {loading && (
            <MDBox py={2} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          <MDBox display="flex" justifyContent="space-between" mb={1.5} mt={0.5}>
            <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
              {idEvento ? `ID Evento: ${idEvento}` : ""}
            </MDTypography>

            <MDButton
              color="success"
              disabled={!temPermissao("Menu Equipamentos", "w")}
              onClick={handleOpenNovo}
            >
              Nova Linha
            </MDButton>
          </MDBox>

          <MDBox sx={{ height: "55vh", overflow: "auto", borderRadius: 1 }}>
            <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          background: "#fff",
                          textAlign: "left",
                          padding: "6px 8px",
                          fontSize: 12,
                          color: "#7b809a",
                          verticalAlign: "bottom",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          width: col.id === "acoes" ? col.width : undefined,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {col.render("Header")}
                          {col.isSorted ? (
                            <span style={{ marginLeft: 6 }}>{col.isSortedDesc ? "▼" : "▲"}</span>
                          ) : null}
                        </div>

                        {!col.disableFilters && (
                          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                            {col.canFilter ? col.render("Filter") : null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            padding: "8px 10px",
                            borderTop: "1px solid #eee",
                            fontSize: 12,
                            color: "#1a2b4c",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MDBox>

          <MDBox mt={1.5} display="flex" justifyContent="flex-end">
            <MDTypography variant="button">
              Subtotal linhas: {fmtMoney(subtotalLinhas)}
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseAll} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Novo/Editar */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? "Editar Linha" : "Nova Linha"}</DialogTitle>

        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.75}>
            <Autocomplete
              options={TIPOS_LINHA}
              value={
                TIPOS_LINHA.includes((form.tipo_linha || "").toUpperCase())
                  ? form.tipo_linha.toUpperCase()
                  : "OUTRO"
              }
              onChange={(_, v) =>
                setForm((p) => ({ ...p, tipo_linha: (v || "OUTRO").toUpperCase() }))
              }
              disableClearable
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />

            <MDInput
              label="Descritivo *"
              value={form.descritivo || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, descritivo: (e.target.value ?? "").toUpperCase() }))
              }
              fullWidth
            />

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
              <MDInput
                label="Quantidade *"
                type="number"
                value={form.quantidade ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, quantidade: e.target.value }))}
                fullWidth
              />

              <MDInput
                label="Preço unitário *"
                type="number"
                value={form.preco_unitario ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, preco_unitario: e.target.value }))}
                fullWidth
              />
            </MDBox>

            <TextField
              size="small"
              fullWidth
              disabled
              value={calcTotalLinha(form.quantidade, form.preco_unitario)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseEdit} color="secondary">
            Cancelar
          </MDButton>

          <MDButton
            onClick={handleSave}
            color="success"
            disabled={
              editando
                ? !temPermissao("Menu Equipamentos", "u")
                : !temPermissao("Menu Equipamentos", "w")
            }
          >
            {editando ? "Guardar" : "Inserir"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmar apagar */}
      <Dialog
        open={openConfirmDel}
        onClose={() => {
          setOpenConfirmDel(false);
          setLinhaParaApagar(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminação</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2">Apagar esta linha?</MDTypography>

          {linhaParaApagar?.descritivo && (
            <MDTypography variant="button" sx={{ display: "block", mt: 1 }}>
              {String(linhaParaApagar.descritivo)}
            </MDTypography>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenConfirmDel(false);
              setLinhaParaApagar(null);
            }}
            color="secondary"
          >
            Cancelar
          </MDButton>

          <MDButton onClick={confirmarApagar} color="error">
            Apagar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
